<template>
  <div class="bp-preview">
    <!-- Empty: no groups -->
    <div v-if="!groups.length" class="bp-empty-state">
      <MfEmpty
          title="Hali guruh yaratilmagan"
          description="Byudjet jarayoni chizmasini ko'rish uchun avval guruh yarating (masalan, Tuzish, Ijro, Hisobdorlik)"
      />
      <div class="bp-empty-state__action">
        <el-button type="primary" @click="$emit('create-group')">
          <span class="bp-btn-plus">+</span> Guruh yaratish
        </el-button>
      </div>
    </div>

    <template v-else>
      <!-- Toolbar: legend + timeline range -->
      <div class="bp-chart-bar">
        <div class="bp-legend">
          <span
              v-for="g in sortedGroups"
              :key="g.id"
              class="bp-legend__item"
              :class="{'is-inactive': g.is_active === false}"
          >
            <span class="bp-legend__dot" :style="{background: g.color || '#64748B'}"/>
            {{ groupTitle(g) }}
          </span>
        </div>
        <div v-if="columnsCount" class="bp-range">
          <span class="bp-range__ico">🗓</span>
          {{ rangeCaption }}
          <span class="bp-range__sep">·</span>
          {{ columnsCount }} oy
        </div>
      </div>

      <p v-if="!columnsCount" class="bp-hint">
        Vaqt chizig'ini ko'rish uchun kamida bitta band (item) qo'shing — davr guruhlardagi
        bandlar asosida avtomatik hisoblanadi.
      </p>

      <!-- Gantt grid -->
      <div v-if="columnsCount" class="bp-grid-scroll">
        <div
            class="bp-grid"
            :style="{
              gridTemplateColumns: `220px repeat(${columnsCount}, minmax(104px, 1fr))`,
              gridTemplateRows: `34px repeat(${totalLaneRows}, minmax(48px, auto)) 40px`,
            }"
        >
          <!-- top-left corner (year band) -->
          <div class="bp-corner bp-corner--top" style="grid-row: 1; grid-column: 1;">
            <span>Guruh</span>
          </div>

          <!-- year header (top) -->
          <div
              v-for="yg in yearGroups"
              :key="`y-${yg.year}-${yg.startIndex}`"
              class="bp-year-cell"
              :style="{gridRow: 1, gridColumn: `${yg.startIndex + 2} / span ${yg.span}`}"
          >
            {{ yg.year }}
          </div>

          <!-- rows -->
          <template v-for="row in rows" :key="row.group.id">
            <!-- row band background -->
            <div
                class="bp-row-band"
                :class="{'is-odd': row.index % 2 === 1}"
                :style="{gridRow: `${row.rowStart} / span ${row.laneCount}`, gridColumn: `1 / span ${columnsCount + 1}`}"
            />
            <!-- month gridlines -->
            <div
                v-for="(col, colIndex) in monthColumns"
                :key="`gl-${row.group.id}-${colIndex}`"
                class="bp-gridline"
                :class="{'is-year-start': col.month === 1 && colIndex !== 0}"
                :style="{gridRow: `${row.rowStart} / span ${row.laneCount}`, gridColumn: colIndex + 2}"
            />

            <!-- sticky group label -->
            <div
                class="bp-group-label"
                :class="{'is-inactive': row.group.is_active === false}"
                :style="{gridRow: `${row.rowStart} / span ${row.laneCount}`, gridColumn: 1, borderLeftColor: row.group.color || '#64748B'}"
            >
              <span class="bp-group-label__text">{{ groupTitle(row.group) }}</span>
              <span class="bp-group-label__count">{{ row.bars.length }}</span>
              <span v-if="row.group.is_active === false" class="bp-badge">Nofaol</span>
            </div>

            <!-- bars -->
            <el-tooltip
                v-for="bar in row.bars"
                :key="bar.item.id"
                :content="bar.tooltip"
                placement="top"
                effect="dark"
            >
              <div
                  class="bp-bar"
                  :class="{'is-inactive': bar.item.is_active === false, 'is-selected': selectedItemId === bar.item.id}"
                  role="button"
                  tabindex="0"
                  :aria-pressed="selectedItemId === bar.item.id"
                  :style="{
                    gridRow: row.rowStart + bar.lane,
                    gridColumn: `${bar.column + 2} / span ${bar.span}`,
                    '--bar-color': row.group.color || '#64748B',
                  }"
                  @click="selectBar(bar.item, row.group)"
                  @keydown.enter.prevent="selectBar(bar.item, row.group)"
                  @keydown.space.prevent="selectBar(bar.item, row.group)"
              >
                <span class="bp-bar__text">{{ bar.title || '...' }}</span>
                <span v-if="bar.item.is_active === false" class="bp-badge bp-badge--bar">Nofaol</span>
              </div>
            </el-tooltip>
          </template>

          <!-- bottom-left corner (aligned with the month axis) -->
          <div class="bp-corner bp-corner--bottom" :style="{gridRow: monthAxisRow, gridColumn: 1}"/>

          <!-- month axis (bottom) -->
          <div
              v-for="(col, colIndex) in monthColumns"
              :key="`m-${colIndex}`"
              class="bp-month-cell"
              :class="{'is-year-start': col.month === 1 && colIndex !== 0}"
              :style="{gridRow: monthAxisRow, gridColumn: colIndex + 2}"
          >
            {{ col.short }}
          </div>
        </div>
      </div>

      <!-- groups exist but no items yet: show label-only rows -->
      <div v-else class="bp-label-only">
        <div
            v-for="row in rows"
            :key="row.group.id"
            class="bp-group-label bp-group-label--standalone"
            :class="{'is-inactive': row.group.is_active === false}"
            :style="{borderLeftColor: row.group.color || '#64748B'}"
        >
          <span class="bp-group-label__text">{{ groupTitle(row.group) }}</span>
          <span v-if="row.group.is_active === false" class="bp-badge">Nofaol</span>
        </div>
      </div>

      <!-- Content panel: opens when a bar is clicked -->
      <p v-if="!selectedItemId && columnsCount" class="bp-panel-hint">
        Batafsil ma'lumot uchun grafikdagi bandni bosing.
      </p>

      <transition name="bp-panel-fade">
        <section v-if="selectedItemId" ref="panelRef" class="bp-panel" :style="{'--accent': selectedColor}">
          <button class="bp-panel__close" title="Yopish" @click="clearSelection">✕</button>

          <!-- loading -->
          <div v-if="contentLoading" class="bp-panel__loading">
            <el-skeleton :rows="4" animated/>
          </div>

          <!-- error -->
          <div v-else-if="contentError" class="bp-panel__state">
            <p>Ma'lumotni yuklab bo'lmadi.</p>
            <el-button size="small" type="primary" plain @click="loadContent(selectedItemId)">Qayta urinish</el-button>
          </div>

          <!-- content -->
          <template v-else-if="panel">
            <header class="bp-panel__head">
              <h3 class="bp-panel__title">{{ panel.title }}</h3>
              <p class="bp-panel__meta"><span class="bp-panel__meta-ico">🗓</span>{{ panel.period }}</p>
            </header>
            <div v-if="panel.empty" class="bp-panel__state bp-panel__state--muted">
              Bu band uchun batafsil kontent kiritilmagan.
            </div>
            <article v-else class="bp-panel__body markdown-body" v-html="panel.html"/>
          </template>
        </section>
      </transition>
    </template>
  </div>
</template>

<script>
import {marked} from "marked";
import DOMPurify from "dompurify";
import MfEmpty from "@/components/core/MfEmpty.vue";
import {API_BASE, absMonth, displayLangText, periodLabel, MONTH_NAMES_FULL, MONTH_NAMES_SHORT} from "./budgetProcessTypes";

marked.setOptions({gfm: true, breaks: false});

// Greedy interval-scheduling lane packing: bars whose month ranges overlap
// get pushed to a new lane instead of stacking on top of each other. Mutates
// each bar with a `lane` index (0-based) and returns the lane count.
function assignLanes(bars) {
  const sorted = [...bars].sort((a, b) => a.column - b.column || a.span - b.span);
  const laneEnds = []; // laneEnds[i] = exclusive end column of the lane's last bar.
  sorted.forEach((bar) => {
    const barEnd = bar.column + bar.span;
    let lane = laneEnds.findIndex((end) => end <= bar.column);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(barEnd);
    } else {
      laneEnds[lane] = barEnd;
    }
    bar.lane = lane;
  });
  return Math.max(1, laneEnds.length);
}

export default {
  name: "BudgetProcessPreview",
  components: {MfEmpty},
  props: {
    groups: {type: Array, default: () => []},
    items: {type: Array, default: () => []},
  },
  emits: ["create-group"],
  data() {
    return {
      selectedItemId: null,
      selectedColor: "#64748B",
      contentLoading: false,
      contentError: false,
      panel: null,
      // Per-session cache of fetched item content, keyed by item id.
      contentCache: {},
    };
  },
  computed: {
    sortedGroups() {
      return [...this.groups].sort((a, b) => (a.order_number - b.order_number) || (a.id - b.id));
    },
    timelineStart() {
      if (!this.items.length) return null;
      return Math.min(...this.items.map((i) => absMonth(i.start_year, i.start_month)));
    },
    timelineEnd() {
      if (!this.items.length) return null;
      return Math.max(...this.items.map((i) => absMonth(i.end_year, i.end_month)));
    },
    columnsCount() {
      if (this.timelineStart === null) return 0;
      return this.timelineEnd - this.timelineStart + 1;
    },
    monthColumns() {
      if (this.timelineStart === null) return [];
      const cols = [];
      for (let i = 0; i < this.columnsCount; i++) {
        const abs = this.timelineStart + i;
        const year = Math.floor((abs - 1) / 12);
        const month = abs - year * 12;
        cols.push({year, month, short: MONTH_NAMES_SHORT[month - 1] || month});
      }
      return cols;
    },
    // Consecutive month columns grouped by year → colSpan blocks for the top
    // header row (e.g. "2023" over Dec, "2024" over Jan..Feb).
    yearGroups() {
      const out = [];
      this.monthColumns.forEach((col, idx) => {
        const last = out[out.length - 1];
        if (last && last.year === col.year) {
          last.span += 1;
        } else {
          out.push({year: col.year, startIndex: idx, span: 1});
        }
      });
      return out;
    },
    rangeCaption() {
      if (this.timelineStart === null) return "";
      const first = this.monthColumns[0];
      const last = this.monthColumns[this.monthColumns.length - 1];
      return `${periodLabel(first.year, first.month, MONTH_NAMES_FULL)} — ${periodLabel(last.year, last.month, MONTH_NAMES_FULL)}`;
    },
    // Each group gets its own block of grid rows — one "lane" per bar that
    // can't share a row with an earlier bar because their periods overlap.
    // rowStart/laneCount let the template give every group's label + bars a
    // precise, non-overlapping grid-row placement (see assignLanes below).
    rows() {
      let rowCursor = 2; // grid-row 1 = year band; month axis sits at the bottom.
      return this.sortedGroups.map((group, index) => {
        const bars = this.items
            .filter((item) => item.group_id === group.id)
            .map((item) => {
              const startAbs = absMonth(item.start_year, item.start_month);
              const endAbs = absMonth(item.end_year, item.end_month);
              const title = displayLangText(item.title);
              return {
                item,
                title,
                // 0-based month offset from timelineStart (matches monthColumns'
                // colIndex) — the template adds +2, not +1: grid-column 1 is the
                // label column, so month offset 0 must land on grid-column 2.
                column: startAbs - this.timelineStart,
                span: endAbs - startAbs + 1,
                tooltip: `${title || "..."} · ${periodLabel(item.start_year, item.start_month)} — ${periodLabel(item.end_year, item.end_month)}`,
              };
            });
        const laneCount = assignLanes(bars);
        const rowStart = rowCursor;
        rowCursor += laneCount;
        return {group, bars, laneCount, rowStart, index};
      });
    },
    totalLaneRows() {
      return this.rows.reduce((sum, row) => sum + row.laneCount, 0);
    },
    // Grid row of the bottom month axis: row 1 = year band, then one row per
    // lane, then the axis directly below the last lane.
    monthAxisRow() {
      return this.totalLaneRows + 2;
    },
  },
  methods: {
    groupTitle(group) {
      return displayLangText(group.title) || "...";
    },
    // Toggle selection: clicking the selected bar again collapses the panel.
    selectBar(item, group) {
      if (this.selectedItemId === item.id) {
        this.clearSelection();
        return;
      }
      this.selectedItemId = item.id;
      this.selectedColor = group?.color || "#64748B";
      this.loadContent(item.id);
      this.$nextTick(() => {
        this.$refs.panelRef?.scrollIntoView({behavior: "smooth", block: "nearest"});
      });
    },
    clearSelection() {
      this.selectedItemId = null;
      this.panel = null;
      this.contentError = false;
      this.contentLoading = false;
    },
    // Fetches the full admin item (title + content as MultiLangStr) and renders
    // its markdown. Cached per item for the session. Reuses the admin single-item
    // endpoint — same data as the portal's /items/:id/content (PORTAL.md 2.3),
    // but returns every language under the console's admin auth.
    loadContent(id) {
      if (this.contentCache[id]) {
        this.panel = this.contentCache[id];
        this.contentError = false;
        this.contentLoading = false;
        return;
      }
      this.contentLoading = true;
      this.contentError = false;
      this.panel = null;
      this.$http
          .get(`${API_BASE}/items/${id}`)
          .then(({data}) => {
            const contentText = displayLangText(data.content);
            const panel = {
              id,
              title: displayLangText(data.title) || "...",
              period: `${periodLabel(data.start_year, data.start_month, MONTH_NAMES_FULL)} — ${periodLabel(data.end_year, data.end_month, MONTH_NAMES_FULL)}`,
              html: contentText ? DOMPurify.sanitize(marked.parse(contentText)) : "",
              empty: !contentText,
            };
            this.contentCache[id] = panel;
            // Ignore a late response if the user has since changed selection.
            if (this.selectedItemId === id) this.panel = panel;
          })
          .catch((error) => {
            const status = error?.response?.status;
            const type = error?.response?.data?.type;
            if (status === 404 || type === "NOTFOUND") {
              // Item was removed/unpublished — collapse gracefully.
              this.clearSelection();
            } else {
              this.contentError = true;
            }
          })
          .finally(() => {
            this.contentLoading = false;
          });
    },
  },
  watch: {
    // If the underlying items change (refetch/delete) and the selected item is
    // gone, close the panel so it never shows stale content.
    items(list) {
      if (this.selectedItemId && !list.some((i) => i.id === this.selectedItemId)) {
        this.clearSelection();
      }
    },
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

.bp-empty-state {
  &__action {
    display: flex;
    justify-content: center;
    margin-top: -12px;
    padding-bottom: 24px;
  }
}

.bp-btn-plus {
  font-size: 16px;
  margin-right: 2px;
}

/* ---- chart bar (legend + range) ---- */
.bp-chart-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 14px;
}

.bp-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;

  &__item {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 500;
    color: #475569;

    &.is-inactive {
      opacity: 0.5;
    }
  }

  &__dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    flex-shrink: 0;
  }
}

.bp-range {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 14px;
  background: rgba(0, 119, 145, 0.07);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: $brand;
  white-space: nowrap;

  &__sep {
    color: $muted;
  }
}

.bp-hint {
  padding: 14px 16px;
  margin: 0;
  background: #f6f9fc;
  border: 1px dashed #d5dded;
  border-radius: 10px;
  color: $muted;
  font-size: 13px;
}

/* ---- grid ---- */
.bp-grid-scroll {
  overflow-x: auto;
  border: 1px solid $border;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 30, 80, 0.05);
}

.bp-grid {
  display: grid;
  min-width: max-content;
  background: #fff;
}

.bp-corner {
  position: sticky;
  left: 0;
  z-index: 7;
  display: flex;
  align-items: center;
  padding: 0 14px;
  background: #f6f9fc;
  border-right: 1px solid $border;
  box-shadow: 6px 0 8px -6px rgba(15, 30, 80, 0.12);
  font-size: 14px;
  font-weight: 600;
  color: $navy;

  &--top {
    border-bottom: 1px solid $border;
  }

  &--bottom {
    border-top: 1px solid $border;
  }
}

.bp-year-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eef4f7;
  border-bottom: 1px solid $border;
  border-left: 1px solid #dfe6ee;
  font-family: "Montserrat-Bold", sans-serif;
  font-size: 14px;
  color: $navy;
}

.bp-month-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafbfd;
  border-top: 1px solid $border;
  border-left: 1px solid #f0f2f6;
  font-size: 13px;
  font-weight: 600;
  color: #64748b;

  &.is-year-start {
    border-left: 1px solid #cfd8e6;
  }
}

.bp-row-band {
  background: #fff;
  border-bottom: 1px solid $border;

  &.is-odd {
    background: #fbfcfe;
  }
}

.bp-gridline {
  border-left: 1px solid #f2f4f8;
  pointer-events: none;

  &.is-year-start {
    border-left: 1px solid #d9e0ec;
  }
}

.bp-group-label {
  position: sticky;
  left: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border-left: 4px solid;
  border-right: 1px solid $border;
  border-bottom: 1px solid $border;
  box-shadow: 6px 0 8px -6px rgba(15, 30, 80, 0.12);
  font-size: 13px;
  font-weight: 600;
  color: #303133;

  &--standalone {
    border-right: none;
    box-shadow: none;
    background: #fafbfd;
  }

  &.is-inactive {
    opacity: 0.55;
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    height: 20px;
    padding: 0 6px;
    border-radius: 20px;
    background: #eef1f6;
    color: #64748b;
    font-size: 11px;
    font-weight: 600;
    flex-shrink: 0;
  }
}

.bp-label-only {
  border: 1px solid $border;
  border-radius: 12px;
  overflow: hidden;
}

/* ---- bars ---- */
.bp-bar {
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1;
  margin: 6px 3px;
  padding: 0 10px;
  border-radius: 7px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  min-width: 0;
  cursor: pointer;
  background: var(--bar-color);
  background-image: linear-gradient(180deg, rgba(255, 255, 255, 0.18), rgba(255, 255, 255, 0));
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.16), inset 0 0 0 1px rgba(255, 255, 255, 0.12);
  transition: transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease;

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.05);
    box-shadow: 0 5px 12px rgba(0, 0, 0, 0.22);
    z-index: 2;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--bar-color);
    z-index: 2;
  }

  &.is-selected {
    filter: brightness(1.06);
    box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--bar-color), 0 6px 14px rgba(0, 0, 0, 0.25);
    z-index: 2;
  }

  &.is-inactive {
    opacity: 0.5;
    background-image: repeating-linear-gradient(
        45deg,
        rgba(255, 255, 255, 0.18) 0,
        rgba(255, 255, 255, 0.18) 5px,
        transparent 5px,
        transparent 10px
    );
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.bp-badge {
  flex-shrink: 0;
  padding: 1px 7px;
  border-radius: 10px;
  background: #e6a23c;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.6;

  &--bar {
    margin-left: 6px;
    background: rgba(0, 0, 0, 0.35);
  }
}

/* ---- content panel ---- */
.bp-panel-hint {
  margin: 16px 0 0;
  padding: 12px 16px;
  text-align: center;
  background: #f6f9fc;
  border: 1px dashed #d5dded;
  border-radius: 10px;
  color: $muted;
  font-size: 13px;
}

.bp-panel {
  position: relative;
  margin-top: 18px;
  padding: 22px 26px;
  background: #fff;
  border: 1px solid $border;
  border-left: 4px solid var(--accent);
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(15, 30, 80, 0.07);

  &__close {
    position: absolute;
    top: 14px;
    right: 14px;
    width: 30px;
    height: 30px;
    border: 1px solid $border;
    border-radius: 8px;
    background: #fff;
    color: $muted;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    transition: all 0.15s ease;

    &:hover {
      border-color: #f56c6c;
      color: #f56c6c;
      background: rgba(245, 108, 108, 0.06);
    }
  }

  &__loading {
    padding: 4px 40px 4px 0;
  }

  &__head {
    margin-bottom: 14px;
    padding-right: 36px;
  }

  &__title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 18px;
    line-height: 1.35;
    color: $navy;
  }

  &__meta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 8px 0 0;
    padding: 3px 10px;
    background: rgba(0, 119, 145, 0.07);
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: $brand;
  }

  &__state {
    padding: 18px 0;
    color: $muted;
    font-size: 14px;

    p {
      margin: 0 0 10px;
    }

    &--muted {
      font-style: italic;
    }
  }
}

/* panel transition */
.bp-panel-fade-enter-active,
.bp-panel-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.bp-panel-fade-enter-from,
.bp-panel-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* ---- rendered markdown ---- */
.markdown-body {
  font-size: 14px;
  line-height: 1.7;
  color: #303133;
  word-break: break-word;

  :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
    margin: 1.1em 0 0.5em;
    font-family: "Montserrat-Bold", sans-serif;
    line-height: 1.3;
    color: $navy;
  }

  :deep(h2) {
    font-size: 1.28em;
    padding-bottom: 0.3em;
    border-bottom: 1px solid #eef1f6;
  }

  :deep(h3) { font-size: 1.13em; }

  // "Montserrat" is loaded only at weight 300, so <strong>'s bold isn't
  // synthesized — render it with the real bold face.
  :deep(strong), :deep(b) {
    font-family: "Montserrat-Bold", sans-serif;
    font-weight: 700;
  }

  :deep(p) { margin: 0.6em 0; }

  :deep(p:first-child) { margin-top: 0; }

  :deep(ul), :deep(ol) {
    padding-left: 1.4em;
    margin: 0.6em 0;
  }

  :deep(li) { margin: 0.3em 0; }

  :deep(ul li::marker) { color: $brand; }

  :deep(blockquote) {
    margin: 0.8em 0;
    padding: 6px 16px;
    border-left: 3px solid var(--accent);
    background: #f8fafc;
    border-radius: 0 6px 6px 0;
    color: #555;
  }

  :deep(a) {
    color: $brand;
    text-decoration: underline;
  }

  :deep(table) {
    border-collapse: collapse;
    margin: 0.8em 0;
    width: 100%;
  }

  :deep(th), :deep(td) {
    border: 1px solid $border;
    padding: 6px 10px;
    text-align: left;
  }

  :deep(th) {
    background: #f6f9fc;
    color: $navy;
    font-weight: 600;
  }

  :deep(code) {
    background: #f5f7fa;
    padding: 1px 5px;
    border-radius: 4px;
    font-size: 0.9em;
  }

  :deep(pre) {
    background: #f5f7fa;
    padding: 10px 12px;
    border-radius: 6px;
    overflow: auto;

    code {
      background: none;
      padding: 0;
    }
  }

  :deep(img) {
    max-width: 100%;
  }
}
</style>
